import { useCallback, useMemo, useState } from 'react';

import { Box, Button, Stack, Typography } from '@mui/material';
import { useChannel } from 'ably/react';

import { IncomingCallsApi } from '@/api';
import { DataGrid, Page } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import CallDetailsModal from '@/components/Modals/CallDetailsModal/CallDetailsModal';
import { LocalStoragePrefix } from '@/constants';
import { IncomingCallEvent } from '@/layouts/MainLayout/components/IncomingCall/data';
import { ICall } from '@/models';
import { callRecordsCols } from '@/pages/CallRecords/data';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';
import { getLocalStorageItem } from '@/utils';

const CallRecordsDataGrid = () => {
  const { user, account } = useUserStore();
  const { updateToast } = useToastStore();
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedCall, setSelectedCall] = useState<ICall | null>(null);
  const [isError, setIsError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasExtension, setHasExtension] = useState(false);

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `callRecords-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        return callRecordsCols;
      }

      return mergeColumnSettings(callRecordsCols, savedSettings);
    },
  );

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: ICall,
  ) => {
    event.stopPropagation();
    setSelectedCall(row);
    handleView(row);
  };

  const handleView = (row: ICall) => {
    if (row?._id) {
      setIsModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setRefreshKey((prev) => prev + 1);
  };

  const fetchCalls = useCallback(
    async function (
      page = 1,
      limit = 100,
      filter = '',
    ): Promise<{ rows: ICall[]; totalCount: number }> {
      setIsError(false);
      try {
        const response = await IncomingCallsApi.getCalls({
          page,
          limit,
          filter,
          sort: JSON.stringify({ createdAt: -1 }),
        });

        setHasExtension(!!response.data.hasExtension);

        // Update the selected call if defined to keep the dialog data up-to-date
        if (selectedCall) {
          const updatedCall = response.data.docs.find(
            (call: ICall) => call._id === selectedCall._id,
          );
          if (updatedCall) {
            setSelectedCall(updatedCall);
          }
        }

        return {
          rows: response.data.docs,
          totalCount: response.data.totalDocs,
        };
      } catch (err: any) {
        setIsError(true);
        updateToast({
          type: ConfirmTypes.ERROR,
          open: true,
          message: err.message || 'Error loading units',
        });
        return { rows: [], totalCount: 0 };
      }
    },
    [updateToast, selectedCall],
  );

  useChannel(`account:${account?._id}:incoming-call`, (message) => {
    // Trigger a data refetch when a call has been modified
    if (message.name === IncomingCallEvent.MODIFIED) {
      setRefreshKey((prev) => prev + 1);
    }
  });

  const displayColumnSettings = useMemo<DataGridColSettings[]>(
    () =>
      hasExtension
        ? columnSettings
        : columnSettings.filter((col) => col.field !== 'extension'),
    [hasExtension, columnSettings],
  );

  const columns = useDataGridColumns({
    type: 'callRecords',
    columnSettings: displayColumnSettings,
    openMenu: handleOpenMenu,
  });

  const renderContent = () => {
    if (isError) {
      return (
        <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
          <Typography color="error">
            Error loading units. Please try again.
          </Typography>
          <Button
            variant="contained"
            onClick={() => setRefreshKey((prev) => prev + 1)}
          >
            Retry
          </Button>
        </Stack>
      );
    }

    return (
      <DataGrid
        key={refreshKey}
        name="callRecords"
        fetchData={fetchCalls}
        columnSettings={displayColumnSettings}
        setColumnSettings={setColumnSettings}
        columns={columns}
        onRowClick={(params) => {
          setSelectedCall(params.row);
          setIsModalOpen(true);
        }}
        channelName={`account:${account?._id}:callRecords`}
      />
    );
  };

  return (
    <Page title="Call log records">
      {selectedCall && (
        <CallDetailsModal
          open={isModalOpen}
          onClose={handleModalClose}
          call={selectedCall}
        ></CallDetailsModal>
      )}

      <Box height="100%" paddingTop="14px">
        {renderContent()}
      </Box>
    </Page>
  );
};

export default CallRecordsDataGrid;
